import React, { useMemo } from 'react'
import { DatasetTreeLeftWrapper } from './DownloadData.styles'
import { getReleaseDisplayName, RELEASE_SECTIONS } from '../../Constants/ReleaseDisplayNames'

export { RELEASE_SECTIONS }

/** Normalize API response: accept array or { content/datasets/publications } */
function toArray(data) {
  if (Array.isArray(data)) return data
  if (data && typeof data === 'object') {
    return data.content ?? data.datasets ?? data.publications ?? data.items ?? []
  }
  return []
}

/** Get dataset id from dataset or publication (support multiple API shapes) */
function getDatasetId(dsOrPub) {
  if (!dsOrPub) return null
  const d = dsOrPub.dataset
  return d?.datasetId ?? d?.id ?? dsOrPub.datasetId ?? null
}

/** Get publication id (support id or publicationId) */
function getPublicationId(pub) {
  return pub?.publicationId ?? pub?.id ?? null
}

/** Live → Latest Release; anything else → Previous Release (API: publicationStatus.name) */
export function splitPublicationsByRelease(publications) {
  const list = toArray(publications)
  const latest = list.filter((p) => (p?.publicationStatus?.name ?? '').toLowerCase() !== 'previous release')
  const previous = list.filter((p) => (p?.publicationStatus?.name ?? '').toLowerCase() === 'previous release')
  return { latest, previous }
}

/**
 * Build grouped list: datasets with their publications (dynamic from backend).
 * Publications are sorted by createdDate descending (latest first).
 */
export function buildDatasetGroups(datasets, publications) {
  const dsList = toArray(datasets)
  const pubList = toArray(publications)
  const sortedDatasets = dsList
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
  const byDataset = {}

  pubList.forEach((pub) => {
    const id = getDatasetId(pub)
    if (id == null) return
    if (!byDataset[id]) {
      byDataset[id] = {
        datasetId: id,
        displayName: pub.dataset?.displayName ?? pub.dataset?.name ?? 'Dataset',
        publications: []
      }
    }
    byDataset[id].publications.push(pub)
  })

  sortedDatasets.forEach((ds) => {
    const id = ds.datasetId ?? ds.id
    if (id == null) return
    if (!byDataset[id]) {
      byDataset[id] = {
        datasetId: id,
        displayName: ds.displayName ?? ds.name ?? 'Dataset',
        publications: []
      }
    } else {
      byDataset[id].displayName = ds.displayName ?? ds.name ?? byDataset[id].displayName
    }
  })

  return sortedDatasets
    .filter((ds) => ds.datasetId != null || ds.id != null)
    .map((ds) => {
      const id = ds.datasetId ?? ds.id
      const group = byDataset[id] ?? {
        datasetId: id,
        displayName: ds.displayName ?? ds.name ?? 'Dataset',
        publications: []
      }
      const pubs = (group.publications || []).slice().sort((a, b) => {
        const aD = new Date(a.createdDate ?? 0)
        const bD = new Date(b.createdDate ?? 0)
        return bD - aD
      })
      return { ...group, publications: pubs }
    })
}

/**
 * Get the latest (most recently created) publication for the first dataset.
 * Used to set initial selected publication on load.
 */
export function getDefaultLatestPublication(datasets, publications, isGgcAdmin = false) {
  const groups = buildDatasetGroups(datasets, publications)
  const firstWithPubs = groups.find((g) => g.publications?.length > 0)
  if (!firstWithPubs?.publications?.length) return null
  const pub = firstWithPubs.publications[0]
  return publicationToSelection(pub, isGgcAdmin)
}

/**
 * Prefer a "live" publication for default selection (internal users: Latest Release row stays selected/highlighted).
 * Falls back to null if no live publications; caller should use getDefaultLatestPublication as fallback.
 */
export function getDefaultLatestPublicationPreferLive(datasets, publications, isGgcAdmin = false) {
  const list = toArray(publications)
  const livePubs = list.filter((p) => (p?.publicationStatus?.name ?? '').toLowerCase() === 'live')
  if (livePubs.length === 0) return null
  const groups = buildDatasetGroups(datasets, livePubs)
  const firstWithPubs = groups.find((g) => g.publications?.length > 0)
  if (!firstWithPubs?.publications?.length) return null
  const pub = firstWithPubs.publications[0]
  return publicationToSelection(pub, isGgcAdmin)
}

/** Sidebar row label (stripped year/dataset form for both admin and non-admin). */
export function getSidebarRowDisplayLabel(pub) {
  return getReleaseDisplayName(pub)
}

/** Whether a sidebar row is the active dataset + publication for its release section. */
export function isDatasetRowActive(group, activeDatasetId, activePublicationId, sectionPubs) {
  if (activeDatasetId == null || activePublicationId == null) return false
  if (String(group?.datasetId) !== String(activeDatasetId)) return false
  return (sectionPubs || []).some(
    (p) => String(getPublicationId(p)) === String(activePublicationId)
  )
}

/** Stored selection: admin keeps full API name; non-admin uses stripped display name. */
export function publicationToSelection(pub, isGgcAdmin = false) {
  if (!pub) return null
  return {
    publicationId: getPublicationId(pub),
    datasetId: getDatasetId(pub),
    displayName: isGgcAdmin ? (pub.displayName ?? pub.name ?? '') : getReleaseDisplayName(pub)
  }
}

/** Default publication for one dataset group (prefer live, else most recent). */
export function getDefaultPublicationForGroup(group, isGgcAdmin = false) {
  const pubs = group?.publications || []
  if (!pubs.length) return null
  const livePub = pubs.find((p) => (p?.publicationStatus?.name ?? '').toLowerCase() === 'live')
  return publicationToSelection(livePub ?? pubs[0], isGgcAdmin)
}

/**
 * Resolve which publication to show for a dataset row: stored per-dataset choice, else default.
 */
export function resolvePublicationForDataset(group, storedSelection, isGgcAdmin = false) {
  const pubs = group?.publications || []
  if (!pubs.length) return null
  if (storedSelection?.publicationId != null) {
    const storedPub = pubs.find(
      (p) => String(getPublicationId(p)) === String(storedSelection.publicationId)
    )
    if (storedPub) return publicationToSelection(storedPub, isGgcAdmin)
  }
  return getDefaultPublicationForGroup(group, isGgcAdmin)
}

/** Initial per-dataset publication map (each dataset defaults to live or latest). */
export function buildInitialPublicationByDataset(datasets, publications, isGgcAdmin = false) {
  const groups = buildDatasetGroups(datasets, publications)
  const map = {}
  groups.forEach((group) => {
    const selection = getDefaultPublicationForGroup(group, isGgcAdmin)
    if (selection?.datasetId != null) {
      map[selection.datasetId] = selection
    }
  })
  return map
}

/**
 * One release section for admin: heading + a single clickable row per dataset.
 * Version switching is handled in the dataset info modal (Versions tab).
 */
function AdminReleaseSection({
  section,
  datasetGroups,
  activeDatasetId,
  activePublicationId,
  publicationByDataset,
  isGgcAdmin,
  onSelectPublication,
  onDatasetInfoClick
}) {
  if (!datasetGroups.length) return null

  return (
    <div className="release-section" data-section={section.key}>
      <h3 className="release-section-heading">{section.heading}</h3>
      <div className="dataset-tree dataset-tree-internal" role="tree" aria-label={`Datasets – ${section.heading}`}>
        {datasetGroups.map((group) => {
          const pubs = group.publications || []
          if (!pubs.length) return null

          const storedSelection = publicationByDataset?.[group.datasetId]
          const rowSelection = resolvePublicationForDataset(group, storedSelection, isGgcAdmin)
          if (!rowSelection) return null

          const pubForRow = pubs.find(
            (p) => String(getPublicationId(p)) === String(rowSelection.publicationId)
          ) ?? pubs[0]
          const isSelected = isDatasetRowActive(group, activeDatasetId, activePublicationId, pubs)
          const rowLabel = getSidebarRowDisplayLabel(pubForRow)

          return (
            <div key={group.datasetId} className="dataset-group dataset-group-internal">
              <div className="dataset-group-internal-row-wrap">
                <button
                  type="button"
                  className={`dataset-item dataset-preselected-row ${isSelected ? 'selected' : ''}`}
                  onClick={() => {
                    onSelectPublication(rowSelection)
                  }}
                  aria-pressed={isSelected}
                  aria-label={`Select ${rowLabel}`}
                  title={isGgcAdmin ? (pubForRow.displayName ?? pubForRow.name ?? rowLabel) : rowLabel}
                >
                  {rowLabel}
                </button>
                {onDatasetInfoClick && (
                  <button
                    type="button"
                    className="dataset-info-link dataset-info-link-internal"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      onDatasetInfoClick(pubForRow, group.datasetId)
                    }}
                    aria-label="Dataset information"
                    title="Dataset information"
                  >
                    <span className="dataset-info-icon" aria-hidden>
                      <svg xmlns="http://www.w3.org/2000/svg" height="14" width="14" viewBox="0 0 16 16">
                        <g fill="currentColor" stroke="currentColor">
                          <circle data-color="color-2" data-stroke="none" fill="currentColor" cx="8.5" cy="5" r="1" stroke="none" />
                          <circle fill="none" stroke="#333333" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" cx="8.5" cy="8.5" r="7" />
                          <line data-color="color-2" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" x1="8.5" y1="12.5" x2="8.5" y2="8.5" />
                        </g>
                      </svg>
                    </span>
                  </button>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/**
 * Left pane (admin): Latest Release and Previous Release sections; each section has one row per
 * dataset with a dropdown showing all versions for that release.
 */
const DatasetTreeLeft = ({
  datasets = [],
  allPublications = [],
  activeDatasetId,
  activePublicationId,
  publicationByDataset = {},
  isGgcAdmin = false,
  onSelectPublication,
  onDatasetInfoClick
}) => {
  const { latest: latestPubs, previous: previousPubs } = useMemo(
    () => splitPublicationsByRelease(allPublications),
    [allPublications]
  )

  const latestGroups = useMemo(
    () => buildDatasetGroups(datasets, latestPubs),
    [datasets, latestPubs]
  )
  const previousGroups = useMemo(
    () => buildDatasetGroups(datasets, previousPubs),
    [datasets, previousPubs]
  )

  const hasData =
    latestGroups.length > 0 ||
    previousGroups.length > 0 ||
    toArray(datasets).length > 0 ||
    toArray(allPublications).length > 0

  if (!hasData) {
    return (
      <DatasetTreeLeftWrapper>
        <div className="dataset-tree-loading">Loading datasets…</div>
      </DatasetTreeLeftWrapper>
    )
  }

  return (
    <DatasetTreeLeftWrapper>
      <div className="dataset-tree-internal-wrap">
        {RELEASE_SECTIONS.map((section) => {
          const groups = section.key === 'latest' ? latestGroups : previousGroups
          return (
            <AdminReleaseSection
              key={section.key}
              section={section}
              datasetGroups={groups}
              activeDatasetId={activeDatasetId}
              activePublicationId={activePublicationId}
              publicationByDataset={publicationByDataset}
              isGgcAdmin={isGgcAdmin}
              onSelectPublication={onSelectPublication}
              onDatasetInfoClick={onDatasetInfoClick}
            />
          )
        })}
      </div>
    </DatasetTreeLeftWrapper>
  )
}

export default DatasetTreeLeft
